import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Check, Search } from "lucide-react";
import { countryName, buildCountryOptions } from "../lib/countries";
import CountryFlag from "./CountryFlag";

/**
 * Gjenbrukbar landsvelger med flagg, type-ahead-søk, og pin'd top-land.
 *
 * Props:
 *   value: ISO-kode (string) eller null
 *   onChange: (iso | null) => void
 *   allCountries: string[] – ISO-koder fra /search/locations
 *   topCountries: string[] – ISO-koder som skal være pinned øverst
 *   placeholder: tekst når intet er valgt
 *   allowEmpty: boolean – om "alle land"-valg er tillatt (default: true)
 *   compact: boolean – mindre padding (for filter-paneler)
 *   testIdPrefix: string – for data-testid-attributter
 */
export default function CountrySelect({
  value,
  onChange,
  allCountries = [],
  topCountries = ["NO", "SE", "DK", "GB", "US"],
  placeholder,
  allowEmpty = true,
  compact = false,
  testIdPrefix = "country-select",
}) {
  const { i18n, t } = useTranslation();
  const lang = (i18n.language || "no").split("-")[0];
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  // Lukk dropdown ved klikk utenfor
  useEffect(() => {
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Focus search-input når dropdown åpnes
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const { top, rest } = useMemo(
    () => buildCountryOptions(allCountries, topCountries, lang),
    [allCountries, topCountries, lang]
  );

  const filteredTop = useMemo(() => {
    if (!query) return top;
    const q = query.toLowerCase();
    return top.filter(c => c.name.toLowerCase().includes(q) || c.iso.toLowerCase().startsWith(q));
  }, [top, query]);

  const filteredRest = useMemo(() => {
    if (!query) return rest;
    const q = query.toLowerCase();
    return rest.filter(c => c.name.toLowerCase().includes(q) || c.iso.toLowerCase().startsWith(q));
  }, [rest, query]);

  const selectedLabel = value
    ? (
        <span className="inline-flex items-center gap-2">
          <CountryFlag iso={value} size="sm" />
          {countryName(value, lang)}
        </span>
      )
    : (placeholder || t("country_select.placeholder", "Alle land"));

  const pad = compact ? "py-2 px-3" : "py-2.5 px-3";

  const choose = (iso) => {
    onChange(iso);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={rootRef} className="relative" data-testid={testIdPrefix}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full ${pad} flex items-center justify-between gap-2 bg-[var(--noir)] border border-white/15 text-sm text-[var(--cream)] hover:border-[var(--magenta)]/50 transition-colors text-left`}
        data-testid={`${testIdPrefix}-trigger`}
      >
        <span className={value ? "" : "text-[var(--cream-faint)] italic"}>{selectedLabel}</span>
        <ChevronDown className={`h-4 w-4 text-[var(--cream-faint)] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute top-full left-0 right-0 mt-1 bg-[var(--noir-elev)] border border-white/15 shadow-xl max-h-[360px] flex flex-col z-50"
          data-testid={`${testIdPrefix}-menu`}
        >
          <div className="p-2 border-b border-white/10 flex items-center gap-2">
            <Search className="h-3.5 w-3.5 text-[var(--cream-faint)] shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("country_select.search", "Søk land…")}
              className="flex-1 bg-transparent text-sm text-[var(--cream)] outline-none placeholder:text-[var(--cream-faint)] placeholder:italic"
              data-testid={`${testIdPrefix}-search`}
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {allowEmpty && !query && (
              <button
                type="button"
                onClick={() => choose(null)}
                className="w-full px-3 py-2 text-left text-sm hover:bg-white/[0.04] flex items-center gap-2 text-[var(--cream-soft)] italic"
                data-testid={`${testIdPrefix}-option-any`}
              >
                <span className="w-5">🌐</span>
                <span className="flex-1">{t("country_select.any", "Alle land")}</span>
                {value === null && <Check className="h-3.5 w-3.5 text-[var(--magenta)]" />}
              </button>
            )}
            {filteredTop.length > 0 && (
              <>
                {filteredTop.map(c => (
                  <CountryRow key={c.iso} c={c} selected={c.iso === value} onClick={() => choose(c.iso)} testId={`${testIdPrefix}-option-${c.iso}`} />
                ))}
                {filteredRest.length > 0 && <div className="border-t border-white/10 my-1" />}
              </>
            )}
            {filteredRest.map(c => (
              <CountryRow key={c.iso} c={c} selected={c.iso === value} onClick={() => choose(c.iso)} testId={`${testIdPrefix}-option-${c.iso}`} />
            ))}
            {filteredTop.length === 0 && filteredRest.length === 0 && (
              <div className="px-3 py-4 text-sm text-[var(--cream-faint)] italic text-center">
                {t("country_select.no_match", "Ingen treff")}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const CountryRow = ({ c, selected, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-white/[0.04] transition-colors ${selected ? "bg-[var(--magenta)]/10 text-[var(--cream)]" : "text-[var(--cream-soft)]"}`}
    data-testid={testId}
  >
    <CountryFlag iso={c.iso} size="sm" />
    <span className="flex-1 truncate">{c.name}</span>
    {selected && <Check className="h-3.5 w-3.5 text-[var(--magenta)] shrink-0" />}
  </button>
);
